/**
 * Shared PDF rendering styles for consistent display across
 * editor preview and public view
 * 
 * These styles ensure 100% identical rendering of Quill-generated content
 */

export const getPDFRenderStyles = (customCSS = '') => {
  return `
    /* Font families */
    .ql-font-serif {
      font-family: Georgia, Times, 'Times New Roman', serif;
    }
    
    .ql-font-monospace {
      font-family: Monaco, 'Courier New', monospace;
    }
    
    .ql-font-cairo {
      font-family: 'Cairo', sans-serif;
    }
    
    .ql-font-roboto {
      font-family: 'Roboto', sans-serif;
    }
    
    .ql-font-opensans {
      font-family: 'Open Sans', sans-serif;
    }
    
    .ql-font-lato {
      font-family: 'Lato', sans-serif;
    }
    
    .ql-font-montserrat {
      font-family: 'Montserrat', sans-serif;
    }
    
    .ql-font-poppins {
      font-family: 'Poppins', sans-serif;
    }
    
    .ql-font-tajawal {
      font-family: 'Tajawal', sans-serif;
    }
    
    /* Font sizes */
    [style*="font-size: 10px"] {
      font-size: 10px !important;
    }
    
    [style*="font-size: 12px"] {
      font-size: 12px !important;
    }
    
    [style*="font-size: 14px"] {
      font-size: 14px !important;
    }
    
    [style*="font-size: 16px"] {
      font-size: 16px !important;
    }
    
    [style*="font-size: 18px"] {
      font-size: 18px !important;
    }
    
    [style*="font-size: 20px"] {
      font-size: 20px !important;
    }
    
    [style*="font-size: 24px"] {
      font-size: 24px !important;
    }
    
    [style*="font-size: 28px"] {
      font-size: 28px !important;
    }
    
    [style*="font-size: 32px"] {
      font-size: 32px !important;
    }
    
    [style*="font-size: 36px"] {
      font-size: 36px !important;
    }
    
    [style*="font-size: 48px"] {
      font-size: 48px !important;
    }
    
    [style*="font-size: 64px"] {
      font-size: 64px !important;
    }
    
    /* Font weights - CRITICAL for matching editor */
    [style*="font-weight: 100"] {
      font-weight: 100 !important;
    }
    
    [style*="font-weight: 200"] {
      font-weight: 200 !important;
    }
    
    [style*="font-weight: 300"] {
      font-weight: 300 !important;
    }
    
    [style*="font-weight: 400"] {
      font-weight: 400 !important;
    }
    
    [style*="font-weight: 500"] {
      font-weight: 500 !important;
    }
    
    [style*="font-weight: 600"] {
      font-weight: 600 !important;
    }
    
    [style*="font-weight: 700"], strong, b {
      font-weight: 700 !important;
    }
    
    [style*="font-weight: 800"] {
      font-weight: 800 !important;
    }
    
    [style*="font-weight: 900"] {
      font-weight: 900 !important;
    }
    
    /* Text formatting styles */
    em, i {
      font-style: italic !important;
    }
    
    u {
      text-decoration: underline !important;
    }
    
    s, strike {
      text-decoration: line-through !important;
    }
    
    /* Subscript and Superscript */
    sub {
      vertical-align: sub;
      font-size: smaller;
    }
    
    sup {
      vertical-align: super;
      font-size: smaller;
    }
    
    /* Alignment Support */
    .ql-align-left {
      text-align: left !important;
    }
    
    .ql-align-center {
      text-align: center !important;
    }
    
    .ql-align-right {
      text-align: right !important;
    }
    
    .ql-align-justify {
      text-align: justify !important;
    }
    
    /* RTL Support */
    .ql-direction-rtl {
      direction: rtl !important;
      text-align: right !important;
    }
    
    /* Indent handling */
    .ql-indent-1 { padding-left: 3em !important; }
    .ql-indent-2 { padding-left: 6em !important; }
    .ql-indent-3 { padding-left: 9em !important; }
    .ql-indent-4 { padding-left: 12em !important; }
    .ql-indent-5 { padding-left: 15em !important; }
    .ql-indent-6 { padding-left: 18em !important; }
    .ql-indent-7 { padding-left: 21em !important; }
    .ql-indent-8 { padding-left: 24em !important; }
    
    /* Lists */
    ol, ul {
      padding-left: 1.5em;
      margin: 1em 0;
    }
    
    li {
      margin: 0.5em 0;
      line-height: 1.8;
    }
    
    /* Code blocks and inline code */
    code {
      background-color: #f4f4f4;
      padding: 2px 4px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }
    
    pre {
      background-color: #f4f4f4;
      padding: 10px;
      border-radius: 5px;
      overflow-x: auto;
      font-family: 'Courier New', monospace;
    }
    
    pre code {
      background-color: transparent;
      padding: 0;
    }
    
    /* Links */
    a {
      color: #0066cc;
      text-decoration: underline;
    }
    
    a:hover {
      color: #0052a3;
    }
    
    /* Blockquote styling */
    blockquote {
      border-left: 4px solid #ccc;
      margin: 1.5em 10px;
      padding: 0.5em 10px;
      quotes: "\\201C""\\201D""\\2018""\\2019";
      background: #f9f9f9;
      font-style: italic;
      position: relative;
    }
    
    blockquote:before {
      color: #ccc;
      content: open-quote;
      font-size: 4em;
      line-height: 0.1em;
      margin-right: 0.25em;
      vertical-align: -0.4em;
    }
    
    blockquote p {
      display: inline;
    }
    
    /* Tables */
    table {
      border-collapse: collapse;
      width: 100%;
      margin-bottom: 1em;
    }
    
    table th,
    table td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    
    table th {
      background-color: #f8f9fa;
      font-weight: bold;
    }
    
    /* Images */
    img {
      max-width: 100%;
      height: auto;
      display: inline-block;
    }
    
    /* Images with explicit width/height should respect those values */
    img[width] {
      width: attr(width px);
    }
    
    img[height] {
      height: attr(height px);
    }
    
    /* Override max-width for sized images */
    img[width],
    img[style*="width"] {
      max-width: none !important;
    }
    
    /* Image alignment - only when not floating */
    img[data-align="left"]:not([data-float]) {
      display: block;
      margin-left: 0;
      margin-right: auto;
    }
    
    img[data-align="center"]:not([data-float]) {
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
    
    img[data-align="right"]:not([data-float]) {
      display: block;
      margin-left: auto;
      margin-right: 0;
    }
    
    /* Image float - takes precedence over alignment */
    img[data-float="left"] {
      float: left;
      display: inline;
    }
    
    img[data-float="right"] {
      float: right;
      display: inline;
    }
    
    /* Interactive elements - buttons from editor */
    .button-wrapper {
      display: inline-block;
      margin: 5px;
    }
    
    .button-wrapper button {
      padding: 8px 16px;
      border-radius: 4px;
      border: 1px solid #ddd;
      background: linear-gradient(to bottom, #ffffff, #f0f0f0);
      cursor: pointer;
      font-family: inherit;
      font-size: 14px;
      transition: all 0.3s ease;
    }
    
    .button-wrapper button:hover {
      background: linear-gradient(to bottom, #f0f0f0, #e0e0e0);
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    /* Input fields from editor */
    .input-wrapper {
      display: inline-block;
      margin: 5px;
      vertical-align: middle;
    }
    
    .input-wrapper input,
    .input-wrapper textarea,
    .input-wrapper select {
      padding: 6px 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-family: inherit;
      font-size: 14px;
      background: white;
      transition: all 0.3s ease;
    }
    
    .input-wrapper input:focus,
    .input-wrapper textarea:focus,
    .input-wrapper select:focus {
      outline: none;
      border-color: #4CAF50;
      box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
    }
    
    /* Page breaks - hidden in display */
    .page-break, .ql-pagebreak {
      display: none !important;
    }
    
    /* Color and background color handling */
    [style*="color:"] {
      color: inherit;
    }
    
    [style*="background-color:"] {
      background-color: inherit;
    }
    
    /* Custom CSS - Applied last for highest priority */
    ${customCSS}
  `;
};

/**
 * Get font import links for Google Fonts
 */
export const getFontImports = () => {
  return `<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900&family=Roboto:wght@100;300;400;500;700;900&family=Open+Sans:wght@300;400;500;600;700;800&family=Lato:wght@100;300;400;700;900&family=Montserrat:wght@100;200;300;400;500;600;700;800;900&family=Poppins:wght@100;200;300;400;500;600;700;800;900&family=Tajawal:wght@200;300;400;500;700;800;900&display=swap" rel="stylesheet">`;
};